/**
 * Snake – Easter Egg und Trostpflaster bei Fehlern.
 *
 * Eine Hommage an das allererste Programm in diesem Repository
 * (extras/easter-egg/snake.py). Drei Wege dorthin:
 *   - Easter Egg: fuenfmal schnell auf das rote Logo tippen oder "snake"
 *     auf der Tastatur schreiben (watchSnake, watchTypedSnake).
 *   - Fehler: Geht in der App etwas schief, erscheint Snake mit einem
 *     passenden Text (features/error-snake.ts).
 *
 * Farben aus dem Theme, Steuerung mit Pfeiltasten, WASD oder Wischen.
 */
import { h } from '../ui/dom.ts';

const SIZE = 18;
const CELL = 20;
const BEST_KEY = 'sr:snake-best';

export interface SnakeOptions {
  /** Ueberschrift, Standard "Snake". */
  title?: string;
  /** Kurzer Text ueber dem Spiel (z. B. was schiefgelaufen ist). */
  message?: string;
  /** Technische Angabe, aufklappbar. */
  detail?: string;
  /** Zusaetzliche Knoepfe unter dem Spiel. */
  actions?: { label: string; primary?: boolean; onClick: () => void }[];
  onClose?: () => void;
}

/** Fuenf schnelle Tipper auf `mark` oeffnen Snake. */
export function watchSnake(mark: HTMLElement): void {
  let taps: number[] = [];
  mark.addEventListener('click', (ev) => {
    const now = Date.now();
    taps = [...taps.filter((t) => now - t < 1800), now];
    if (taps.length >= 5) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
      taps = [];
      openSnake();
    }
  });
}

/** "snake" auf der Tastatur schreiben oeffnet Snake (einmal beim Start). */
export function watchTypedSnake(): void {
  let typed = '';
  document.addEventListener('keydown', (ev) => {
    if (ev.target instanceof HTMLInputElement || ev.target instanceof HTMLTextAreaElement) return;
    if ((ev.target as HTMLElement | null)?.isContentEditable) return;
    if (ev.key.length !== 1) return;
    typed = (typed + ev.key.toLowerCase()).slice(-5);
    if (typed === 'snake' && !document.querySelector('.snake')) openSnake();
  });
}

function color(name: string, fallback: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

export function openSnake(opts: SnakeOptions = {}): () => void {
  document.querySelector('.snake')?.remove();
  const canvas = h('canvas', { class: 'snake__canvas', attrs: { width: String(SIZE * CELL), height: String(SIZE * CELL) } });
  const score = h('span', { class: 'snake__score', text: '0' });
  let best = 0;
  try {
    best = Number(localStorage.getItem(BEST_KEY) ?? 0) || 0;
  } catch {
    best = 0;
  }
  const bestEl = h('span', { class: 'snake__best', text: `Rekord ${best}` });
  const modal = h(
    'div',
    { class: 'snake', attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': opts.title ?? 'Snake' }, on: { click: (ev) => ev.target === modal && close() } },
    h(
      'div',
      { class: 'snake__panel' },
      h(
        'div',
        { class: 'snake__head' },
        h('strong', { class: 'snake__title', text: opts.title ?? 'Snake' }),
        score,
        bestEl,
        h('button', { class: 'snake__close', text: '×', attrs: { type: 'button', 'aria-label': 'Schließen' }, on: { click: () => close() } }),
      ),
      opts.message ? h('p', { class: 'snake__message', text: opts.message }) : null,
      canvas,
      h('p', { class: 'snake__hint', text: 'Pfeiltasten, WASD oder wischen. Leertaste: Pause.' }),
      opts.detail ? h('details', { class: 'snake__detail' }, h('summary', { text: 'Technische Angabe' }), h('code', { text: opts.detail })) : null,
      opts.actions?.length
        ? h(
            'div',
            { class: 'snake__actions' },
            ...opts.actions.map((a) =>
              h('button', {
                class: 'btn btn--small' + (a.primary ? ' btn--primary' : ''),
                text: a.label,
                attrs: { type: 'button' },
                on: { click: a.onClick },
              }),
            ),
          )
        : null,
    ),
  );
  document.body.append(modal);

  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  const ink = color('--c-ink', '#222');
  const accent = color('--c-accent', '#c96442');
  const grid = color('--c-border', '#ddd');
  const ground = color('--c-surface-2', '#fff');

  const startBody = () => [
    { x: 8, y: 9 },
    { x: 7, y: 9 },
    { x: 6, y: 9 },
  ];
  let snake = startBody();
  let dir = { x: 1, y: 0 };
  let next = dir;
  let apple = place();
  let points = 0;
  let paused = false;
  let over = false;
  let timer = 0;

  function place(): { x: number; y: number } {
    for (;;) {
      const p = { x: Math.floor(Math.random() * SIZE), y: Math.floor(Math.random() * SIZE) };
      if (!snake.some((s) => s.x === p.x && s.y === p.y)) return p;
    }
  }

  function draw(): void {
    ctx.fillStyle = ground;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = grid;
    ctx.globalAlpha = 0.35;
    for (let i = 1; i < SIZE; i++) {
      ctx.beginPath();
      ctx.moveTo(i * CELL, 0);
      ctx.lineTo(i * CELL, canvas.height);
      ctx.moveTo(0, i * CELL);
      ctx.lineTo(canvas.width, i * CELL);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.fillStyle = accent;
    ctx.beginPath();
    ctx.arc(apple.x * CELL + CELL / 2, apple.y * CELL + CELL / 2, CELL / 2 - 3, 0, Math.PI * 2);
    ctx.fill();
    snake.forEach((s, i) => {
      ctx.fillStyle = ink;
      ctx.globalAlpha = i === 0 ? 1 : Math.max(0.35, 1 - i * 0.04);
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(s.x * CELL + 2, s.y * CELL + 2, CELL - 4, CELL - 4, 5);
      else ctx.rect(s.x * CELL + 2, s.y * CELL + 2, CELL - 4, CELL - 4);
      ctx.fill();
    });
    ctx.globalAlpha = 1;
    if (over || paused) {
      ctx.fillStyle = ground;
      ctx.globalAlpha = 0.8;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 1;
      ctx.fillStyle = ink;
      ctx.font = '600 22px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(over ? 'Vorbei – Tippen für neu' : 'Pause', canvas.width / 2, canvas.height / 2);
    }
  }

  function step(): void {
    if (paused || over) return;
    dir = next;
    const head = { x: (snake[0]!.x + dir.x + SIZE) % SIZE, y: (snake[0]!.y + dir.y + SIZE) % SIZE };
    if (snake.some((s) => s.x === head.x && s.y === head.y)) {
      over = true;
      if (points > best) {
        best = points;
        try {
          localStorage.setItem(BEST_KEY, String(best));
        } catch {
          /* egal */
        }
        bestEl.textContent = `Rekord ${best}`;
      }
      draw();
      return;
    }
    snake = [head, ...snake];
    if (head.x === apple.x && head.y === apple.y) {
      points++;
      score.textContent = String(points);
      apple = place();
    } else {
      snake.pop();
    }
    draw();
    clearTimeout(timer);
    timer = window.setTimeout(step, Math.max(70, 150 - points * 3));
  }

  function restart(): void {
    snake = startBody();
    dir = next = { x: 1, y: 0 };
    points = 0;
    score.textContent = '0';
    apple = place();
    over = false;
    paused = false;
    step();
  }

  function turn(x: number, y: number): void {
    if (x === -dir.x && y === -dir.y) return;
    next = { x, y };
  }

  const onKey = (ev: KeyboardEvent) => {
    const k = ev.key.toLowerCase();
    if (k === 'escape') return close();
    if (k === ' ') {
      ev.preventDefault();
      if (over) return restart();
      paused = !paused;
      draw();
      if (!paused) step();
      return;
    }
    const map: Record<string, [number, number]> = { arrowup: [0, -1], w: [0, -1], arrowdown: [0, 1], s: [0, 1], arrowleft: [-1, 0], a: [-1, 0], arrowright: [1, 0], d: [1, 0] };
    const m = map[k];
    if (m) {
      ev.preventDefault();
      turn(m[0], m[1]);
    }
  };
  document.addEventListener('keydown', onKey);

  let start: { x: number; y: number } | null = null;
  canvas.addEventListener('pointerdown', (ev) => {
    start = { x: ev.clientX, y: ev.clientY };
    if (over) restart();
  });
  canvas.addEventListener('pointerup', (ev) => {
    if (!start) return;
    const dx = ev.clientX - start.x;
    const dy = ev.clientY - start.y;
    start = null;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 18) return;
    if (Math.abs(dx) > Math.abs(dy)) turn(Math.sign(dx), 0);
    else turn(0, Math.sign(dy));
  });

  function close(): void {
    clearTimeout(timer);
    document.removeEventListener('keydown', onKey);
    modal.remove();
    opts.onClose?.();
  }

  draw();
  timer = window.setTimeout(step, 500);
  return close;
}
